/**
 * Computes the source crop rectangle that reproduces CSS `object-fit: cover`
 * when drawn into a `targetWidth x targetHeight` box: the largest centered
 * region of the source that matches the target's aspect ratio.
 */
export const computeCoverRect = (
  sourceWidth: number,
  sourceHeight: number,
  targetWidth: number,
  targetHeight: number,
): { sx: number; sy: number; sw: number; sh: number } => {
  const sourceAspect = sourceWidth / sourceHeight;
  const targetAspect = targetWidth / targetHeight;

  if (sourceAspect > targetAspect) {
    const sw = sourceHeight * targetAspect;
    return { sx: (sourceWidth - sw) / 2, sy: 0, sw, sh: sourceHeight };
  }
  if (sourceAspect < targetAspect) {
    const sh = sourceWidth / targetAspect;
    return { sx: 0, sy: (sourceHeight - sh) / 2, sw: sourceWidth, sh };
  }
  return { sx: 0, sy: 0, sw: sourceWidth, sh: sourceHeight };
};

/**
 * Computes the output size that reproduces CSS `object-fit: contain` when
 * fitting a source into a `targetWidth x targetHeight` box: scales the whole
 * source down (never up) to the largest size that fits within the box while
 * preserving its aspect ratio, with no cropping.
 */
export const computeContainSize = (
  sourceWidth: number,
  sourceHeight: number,
  targetWidth: number,
  targetHeight: number,
): { width: number; height: number } => {
  const scale = Math.min(targetWidth / sourceWidth, targetHeight / sourceHeight);
  return {
    width: Math.max(1, Math.round(sourceWidth * scale)),
    height: Math.max(1, Math.round(sourceHeight * scale)),
  };
};
